"use client";

import React from "react";
import { Database, Lock, ShieldCheck, ArrowRight, Cpu, FileText, History, Network, ArrowDown, Layers3 } from "lucide-react";
import { Card, CardContent, CardDescription, CardHeader, CardTitle } from "@/components/ui/card";
import { useRevealOnScroll } from "@/hooks/useRevealOnScroll";
import { cn } from "@/lib/utils";
export default function ApprovedOnlyAccess() {
  const headerReveal = useRevealOnScroll({
    threshold: 0.1
  });
  const diagramReveal = useRevealOnScroll({
    threshold: 0.2
  });
  const cardsReveal = useRevealOnScroll({
    threshold: 0.2
  });
  const technicalDetails = [{
    icon: <Network className="size-6 text-primary" />,
    title: "Strict state isolation",
    description: "Draft and Review content remain outside the answering pipeline, so the model has no access to unapproved knowledge."
  }, {
    icon: <ShieldCheck className="size-6 text-chart-2" />,
    title: "Approved-only retrieval",
    description: "Only documents that pass your workflow and reach Approved status are indexed and available for retrieval and citation."
  }, {
    icon: <History className="size-6 text-primary" />,
    title: "Traceable governance history",
    description: "State changes from Draft to Review to Approved can be tracked for stronger compliance and operational oversight."
  }];
  return <section id="approved-only-access" className="py-16 bg-background overflow-hidden">
      <div className="max-w-7xl mx-auto px-6 lg:px-8">
        <div ref={headerReveal.ref} className={cn("max-w-3xl mx-auto text-center mb-12 transition-all duration-700", headerReveal.isVisible ? "opacity-100 translate-y-0" : "opacity-0 translate-y-8")}>
          <div className="inline-flex items-center gap-2 rounded-full bg-primary/10 px-3 py-1 text-sm font-medium text-primary mb-6">
            <Layers3 className="size-4" />
            Approved-only access
          </div>

          <h2 className="text-3xl font-bold tracking-tight text-foreground sm:text-4xl mb-4">
            Your AI only sees what your team explicitly approves
          </h2>

          <p className="text-lg text-muted-foreground">
            Technical isolation keeps Draft and Review content out of the retrieval layer.
            That reduces the risk of premature answers, unsupported claims, and knowledge leakage into production responses.
          </p>
        </div>

        <div ref={diagramReveal.ref} className={cn("mb-16 p-8 lg:p-12 rounded-2xl bg-muted/30 border border-border transition-all duration-1000 delay-100", diagramReveal.isVisible ? "opacity-100 translate-y-0" : "opacity-0 translate-y-12")}>
          <div className="grid grid-cols-1 lg:grid-cols-[320px_1fr_320px] gap-8 lg:gap-4 items-end">
            <div className="flex flex-col gap-4 relative z-10">
              <div className="text-sm font-semibold text-muted-foreground uppercase tracking-wider mb-2 px-2">
                Content repository
              </div>

              <div className="flex items-center justify-between p-4 rounded-xl border border-dashed border-muted-foreground/40 bg-background/50 backdrop-blur-sm">
                <div className="flex items-center gap-3">
                  <div className="p-2 rounded-md bg-muted">
                    <FileText className="size-5 text-muted-foreground" />
                  </div>
                  <div>
                    <div className="font-medium text-foreground">Draft</div>
                    <div className="text-xs text-muted-foreground">Work in progress</div>
                  </div>
                </div>
                <Lock className="size-5 text-destructive" />
              </div>

              <div className="flex items-center justify-between p-4 rounded-xl border border-dashed border-muted-foreground/40 bg-background/50 backdrop-blur-sm">
                <div className="flex items-center gap-3">
                  <div className="p-2 rounded-md bg-muted">
                    <Database className="size-5 text-muted-foreground" />
                  </div>
                  <div>
                    <div className="font-medium text-foreground">In Review</div>
                    <div className="text-xs text-muted-foreground">Pending approval</div>
                  </div>
                </div>
                <Lock className="size-5 text-destructive" />
              </div>

              <div className="flex items-center justify-between p-4 rounded-xl border-2 border-chart-2 bg-background shadow-sm relative group">
                <div className="absolute inset-0 bg-chart-2/5 rounded-xl opacity-0 group-hover:opacity-100 transition-opacity" />
                <div className="flex items-center gap-3 relative z-10">
                  <div className="p-2 rounded-md bg-chart-2/10">
                    <Database className="size-5 text-chart-2" />
                  </div>
                  <div>
                    <div className="font-medium text-foreground">Approved</div>
                    <div className="text-xs text-muted-foreground">Production ready</div>
                  </div>
                </div>
                <ShieldCheck className="size-6 text-chart-2 relative z-10" />
              </div>
            </div>

            <div className="flex justify-center items-center lg:h-[88px]">
              <div className="lg:hidden flex flex-col items-center py-4">
                <div className="w-1 h-8 bg-chart-2/30 rounded-full mb-2" />
                <ArrowDown className="size-8 text-chart-2" />
              </div>

              <div className="hidden lg:flex w-full items-center relative px-4">
                <div className="h-1.5 w-full bg-chart-2/20 rounded-full overflow-hidden relative">
                  <div className="absolute top-0 left-0 h-full w-full bg-chart-2 animate-[pulse_2s_cubic-bezier(0.4,0,0.6,1)_infinite] origin-left" />
                </div>
                <ArrowRight className="size-8 text-chart-2 absolute right-0 translate-x-1/2 bg-muted/30 rounded-full p-1" />
              </div>
            </div>

            <div className="flex flex-col justify-end relative z-10">
              <div className="text-sm font-semibold text-muted-foreground uppercase tracking-wider mb-2 px-2 lg:text-right">
                Retrieval engine
              </div>
              <div className="p-6 rounded-xl border-2 border-primary bg-primary/5 flex flex-col items-center text-center gap-4 shadow-[0_0_40px_-10px_rgba(var(--primary),0.15)]">
                <div className="p-4 rounded-full bg-primary/10 text-primary ring-4 ring-primary/5">
                  <Cpu className="size-10" />
                </div>
                <div>
                  <h4 className="font-semibold text-lg text-foreground">EloRAG AI</h4>
                  <p className="text-sm text-muted-foreground mt-1">
                    Answers generated only from approved, cited sources.
                  </p>
                </div>
              </div>
            </div>
          </div>
        </div>

        <div ref={cardsReveal.ref} className="grid grid-cols-1 md:grid-cols-3 gap-6">
          {technicalDetails.map((detail, i) => <Card key={i} data-index={i} className={cn("bg-card border-border shadow-sm transition-all duration-700 hover:shadow-md", cardsReveal.isVisible ? "opacity-100 translate-y-0" : "opacity-0 translate-y-8")} style={{
          transitionDelay: `${i * 150 + 300}ms`
        }}>
              <CardHeader>
                <div className="mb-4 inline-flex p-3 rounded-lg bg-muted/50 border border-border/50">
                  {detail.icon}
                </div>
                <CardTitle className="text-xl">{detail.title}</CardTitle>
              </CardHeader>
              <CardContent>
                <CardDescription className="text-base leading-relaxed">
                  {detail.description}
                </CardDescription>
              </CardContent>
            </Card>)}
        </div>
      </div>
    </section>;
}